import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { 
  Loader2, 
  Plus, 
  Trash2, 
  Download, 
  Sparkles,
  Save,
  X,
  FileText,
  Upload,
  Image,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Crop,
  Library,
  Lock,
  Type,
  LayoutTemplate,
  BookmarkPlus,
  FolderOpen,
  Landmark,
  Users,
  Search,
  UserPlus,
  Edit2,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import PricingModal from '../components/PricingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TEMPLATES = [
  { id: 1, name: 'Minimal Clean', color: '#ffffff', textColor: '#000000' },
  { id: 2, name: 'Bold/Dark Creative', color: '#1a1a1a', textColor: '#ffffff' },
  { id: 3, name: 'Professional Corporate', color: '#ffffff', textColor: '#1a365d' },
  { id: 4, name: 'Event/DJ-Specific', color: '#0f0f0f', textColor: '#ff4500' },
  { id: 5, name: 'Deposit + Balance (Single)', color: '#ffffff', textColor: '#2d3748' },
  { id: 6, name: 'Deposit + Balance (Multi)', color: '#fafafa', textColor: '#805ad5' },
  { id: 7, name: 'Luxury/Premium', color: '#1a1a2e', textColor: '#d4af37' },
  { id: 8, name: 'Modern Color Pop', color: '#ffffff', textColor: '#e53e3e' },
  { id: 9, name: 'Freelancer-Friendly', color: '#f7fafc', textColor: '#4299e1' },
  { id: 10, name: 'Compact/Mobile', color: '#ffffff', textColor: '#38a169' },
];

const CURRENCIES = [
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'AED', label: 'AED (د.إ)', symbol: 'د.إ' },
  { value: 'CAD', label: 'CAD ($)', symbol: '$' },
  { value: 'AUD', label: 'AUD ($)', symbol: '$' },
  { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
  { value: 'INR', label: 'INR (₹)', symbol: '₹' },
  { value: 'CHF', label: 'CHF (Fr)', symbol: 'Fr' },
  { value: 'ZAR', label: 'ZAR (R)', symbol: 'R' },
  { value: 'NGN', label: 'NGN (₦)', symbol: '₦' },
  { value: 'SEK', label: 'SEK (kr)', symbol: 'kr' },
  { value: 'NOK', label: 'NOK (kr)', symbol: 'kr' },
  { value: 'DKK', label: 'DKK (kr)', symbol: 'kr' },
  { value: 'SGD', label: 'SGD ($)', symbol: '$' },
  { value: 'HKD', label: 'HKD ($)', symbol: '$' },
  { value: 'NZD', label: 'NZD ($)', symbol: '$' },
  { value: 'BRL', label: 'BRL (R$)', symbol: 'R$' },
  { value: 'MXN', label: 'MXN ($)', symbol: '$' },
  { value: 'PLN', label: 'PLN (zł)', symbol: 'zł' },
  { value: 'TRY', label: 'TRY (₺)', symbol: '₺' },
  { value: 'KES', label: 'KES (KSh)', symbol: 'KSh' },
  { value: 'GHS', label: 'GHS (₵)', symbol: '₵' },
  { value: 'SAR', label: 'SAR (﷼)', symbol: '﷼' },
  { value: 'QAR', label: 'QAR (﷼)', symbol: '﷼' },
  { value: 'KWD', label: 'KWD (د.ك)', symbol: 'د.ك' },
  { value: 'BHD', label: 'BHD (BD)', symbol: 'BD' },
  { value: 'OMR', label: 'OMR (﷼)', symbol: '﷼' },
  { value: 'JMD', label: 'JMD ($)', symbol: '$' },
  { value: 'TTD', label: 'TTD ($)', symbol: '$' },
];

const DEFAULT_TERMS = `Payment Terms:
• A non-refundable deposit of 50% is required to secure the booking.
• Remaining balance is due 7 days before the event date.
• Late payments will incur a 5% weekly fee.
• Cancellation within 14 days of the event forfeits the full deposit.
• Client must provide all necessary equipment access and requirements in advance.`;

const FONTS = [
  { id: 'Inter', name: 'Inter', category: 'Sans-serif', preview: "'Inter', sans-serif" },
  { id: 'Playfair Display', name: 'Playfair Display', category: 'Serif', preview: "'Playfair Display', serif" },
  { id: 'Montserrat', name: 'Montserrat', category: 'Sans-serif', preview: "'Montserrat', sans-serif" },
  { id: 'Roboto Slab', name: 'Roboto Slab', category: 'Serif', preview: "'Roboto Slab', serif" },
  { id: 'Space Grotesk', name: 'Space Grotesk', category: 'Sans-serif', preview: "'Space Grotesk', sans-serif" },
];

const HEADER_STYLES = [
  { id: 'classic', name: 'Classic', desc: 'Bold accent border' },
  { id: 'banner', name: 'Banner', desc: 'Full-width colour' },
  { id: 'minimal', name: 'Minimal', desc: 'Light thin line' },
  { id: 'boxed', name: 'Boxed', desc: 'Bordered frame' },
];

const INVOICE_STORAGE_KEY = 'sif_invoice_form';

export default function InvoiceCreatorPage() {
  const { isAuthenticated, planInfo, fetchPlanInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [improvingTerms, setImprovingTerms] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savedLogos, setSavedLogos] = useState([]);
  const [showLogoLibrary, setShowLogoLibrary] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: undefined });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingTrigger, setPricingTrigger] = useState(null);
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [showLoadPreset, setShowLoadPreset] = useState(false);
  const [savedClients, setSavedClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showClientManager, setShowClientManager] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ name: '', company: '', email: '', phone: '', address: '', vat_number: '' });
  const clientSearchRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const plan = planInfo?.plan || 'free';
  const allowedTemplates = planInfo?.limits?.allowed_templates || [1, 2, 3];
  const showWatermark = planInfo?.limits?.watermark !== false;
  const credits = planInfo?.credits || 0;
  const downloadCost = planInfo?.credit_costs?.download || 1;
  const termsCost = planInfo?.credit_costs?.invoice_terms || 1;

  // Restore saved state (template + form data)
  const savedState = React.useMemo(() => {
    try {
      const saved = localStorage.getItem(INVOICE_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return null;
  }, []);

  const [selectedTemplate, setSelectedTemplate] = useState(savedState?.template || 1);
  
  const [formData, setFormData] = useState(savedState?.form || {
    business_name: '',
    business_address: '',
    business_email: '',
    business_phone: '',
    logo_path: '',
    logo_position: 'left',
    font_family: 'Inter',
    header_style: 'classic',
    include_banking: false,
    bank_account_number: '',
    bank_sort_code: '',
    bank_iban: '',
    bank_swift: '',
    client_name: '',
    client_address: '',
    client_email: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    event_date: '',
    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    deposit: 0,
    balance_due: 0,
    currency: 'GBP',
    payment_method: 'Bank Transfer',
    terms: DEFAULT_TERMS,
    notes: '',
  });

  // Auto-save form state to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(INVOICE_STORAGE_KEY, JSON.stringify({ form: formData, template: selectedTemplate }));
    }, 500);
    return () => clearTimeout(timer);
  }, [formData, selectedTemplate]);

  // Fetch saved logos on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchSavedLogos();
      fetchPresets();
      fetchSavedClients();
    }
  }, [isAuthenticated]);

  const fetchSavedLogos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/logos`, { withCredentials: true });
      setSavedLogos(response.data.logos || []);
    } catch (error) {
      console.error('Failed to fetch logos:', error);
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoice-templates`, { withCredentials: true });
      setSavedPresets(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/invoice-templates`, {
        name: presetName.trim(),
        template_id: selectedTemplate,
        font_family: formData.font_family,
        header_style: formData.header_style,
        business_name: formData.business_name || null,
        business_address: formData.business_address || null,
        business_email: formData.business_email || null,
        business_phone: formData.business_phone || null,
        logo_path: formData.logo_path || null,
        logo_position: formData.logo_position,
        include_banking: formData.include_banking,
        bank_account_number: formData.bank_account_number || null,
        bank_sort_code: formData.bank_sort_code || null,
        bank_iban: formData.bank_iban || null,
        bank_swift: formData.bank_swift || null,
        currency: formData.currency,
        payment_method: formData.payment_method || null,
        terms: formData.terms || null,
        notes: formData.notes || null,
      }, { withCredentials: true });
      toast.success('Template saved!');
      setShowSavePreset(false);
      setPresetName('');
      fetchPresets();
    } catch {
      toast.error('Failed to save template');
    }
  };

  const handleLoadPreset = (preset) => {
    setSelectedTemplate(preset.template_id || 1);
    setFormData(prev => ({
      ...prev,
      font_family: preset.font_family || 'Inter',
      header_style: preset.header_style || 'classic',
      business_name: preset.business_name || prev.business_name,
      business_address: preset.business_address || prev.business_address,
      business_email: preset.business_email || prev.business_email,
      business_phone: preset.business_phone || prev.business_phone,
      logo_path: preset.logo_path || prev.logo_path,
      logo_position: preset.logo_position || prev.logo_position,
      include_banking: preset.include_banking || false,
      bank_account_number: preset.bank_account_number || '',
      bank_sort_code: preset.bank_sort_code || '',
      bank_iban: preset.bank_iban || '',
      bank_swift: preset.bank_swift || '',
      currency: preset.currency || prev.currency,
      payment_method: preset.payment_method || prev.payment_method,
      terms: preset.terms || prev.terms,
      notes: preset.notes || prev.notes,
    }));
    if (preset.logo_path) {
      setLogoPreview(`${API_URL}/api/files/${preset.logo_path}`);
    }
    setShowLoadPreset(false);
    toast.success(`Loaded "${preset.name}"`);
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await axios.delete(`${API_URL}/api/invoice-templates/${presetId}`, { withCredentials: true });
      setSavedPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  // ==================== Saved Clients ====================
  const fetchSavedClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-clients`, { withCredentials: true });
      setSavedClients(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch saved clients:', error);
    }
  };

  const handleSaveClient = async () => {
    const clientData = {
      name: formData.client_name?.trim(),
      company: formData.client_name?.trim(),
      email: formData.client_email?.trim() || null,
      phone: null,
      address: formData.client_address?.trim() || null,
      vat_number: null,
    };
    if (!clientData.name) { toast.error('Client name is required'); return; }
    try {
      await axios.post(`${API_URL}/api/saved-clients`, clientData, { withCredentials: true });
      toast.success('Client saved');
      fetchSavedClients();
    } catch { toast.error('Failed to save client'); }
  };

  const handleSelectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client_name: client.company || client.name,
      client_email: client.email || '',
      client_address: client.address || '',
    }));
    setClientSearch('');
    setShowClientDropdown(false);
    toast.success(`Loaded ${client.name}`);
  };

  const handleSaveClientFromManager = async () => {
    if (!clientForm.name?.trim()) { toast.error('Client name is required'); return; }
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/api/saved-clients/${editingClient.id}`, clientForm, { withCredentials: true });
        toast.success('Client updated');
      } else {
        await axios.post(`${API_URL}/api/saved-clients`, { ...clientForm, company: clientForm.company || clientForm.name }, { withCredentials: true });
        toast.success('Client added');
      }
      setEditingClient(null);
      setClientForm({ name: '', company: '', email: '', phone: '', address: '', vat_number: '' });
      fetchSavedClients();
    } catch { toast.error('Failed to save client'); }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await axios.delete(`${API_URL}/api/saved-clients/${clientId}`, { withCredentials: true });
      setSavedClients(prev => prev.filter(c => c.id !== clientId));
      toast.success('Client deleted');
    } catch { toast.error('Failed to delete client'); }
  };

  const filteredClients = savedClients.filter(c =>
    c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.company?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  // Calculate totals when items change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const balance_due = subtotal - (formData.deposit || 0);
    setFormData(prev => ({ ...prev, subtotal, balance_due }));
  }, [formData.items, formData.deposit]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Calculate amount
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
    }
    
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      return;
    }

    // Create preview and open crop dialog
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImage(e.target.result);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const cropWidth = Math.min(width, 300);
    const cropHeight = Math.min(height, 150);
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;
    
    setCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight
    });
  }, []);

  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return null;
    }

    const canvas = canvasRef.current;
    const image = imgRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png', 1);
    });
  }, [completedCrop]);

  const handleCropComplete = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to upload logos');
      return;
    }

    const blob = await getCroppedImg();
    if (!blob) {
      toast.error('Failed to crop image');
      return;
    }

    // Create preview from cropped image
    const croppedUrl = URL.createObjectURL(blob);
    setLogoPreview(croppedUrl);
    setShowCropDialog(false);

    // Upload cropped image
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, 'logo.png');

      const response = await axios.post(
        `${API_URL}/api/upload/logo`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setFormData(prev => ({ ...prev, logo_path: response.data.path }));
      fetchSavedLogos(); // Refresh logo library
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
      setLogoPreview(null);
    }
    setUploadingLogo(false);
  };

  const handleSelectFromLibrary = (logo) => {
    // Set the logo preview by fetching from API
    setLogoPreview(`${API_URL}/api/files/${logo.storage_path}`);
    setFormData(prev => ({ ...prev, logo_path: logo.storage_path }));
    setShowLogoLibrary(false);
    toast.success('Logo selected!');
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_path: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImproveTerms = async () => {
    if (!formData.terms.trim()) {
      toast.error('Please enter some terms to improve');
      return;
    }

    setImprovingTerms(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/invoices/improve-terms`,
        { current_terms: formData.terms, tone: 'professional' },
        { withCredentials: true }
      );
      setFormData(prev => ({ ...prev, terms: response.data.improved_terms }));
      toast.success('Terms improved!');
    } catch (error) {
      toast.error('Failed to improve terms');
    }
    setImprovingTerms(false);
  };

  const handleSaveInvoice = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save invoices');
      return;
    }

    if (!formData.business_name || !formData.client_name || formData.items.length === 0) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/invoices`,
        { ...formData, template_id: selectedTemplate },
        { withCredentials: true }
      );
      toast.success('Invoice saved!');
      localStorage.removeItem(INVOICE_STORAGE_KEY);
      fetchPlanInfo();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setPricingTrigger('no_credits');
        setShowPricing(true);
      } else {
        toast.error(detail || 'Failed to save invoice');
      }
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to download PDF');
      return;
    }
    if (credits < downloadCost) {
      setPricingTrigger('no_credits');
      setShowPricing(true);
      return;
    }
    setLoading(true);
    try {
      const tempInvoiceData = { ...formData, template_id: selectedTemplate };
      
      const saveResponse = await axios.post(
        `${API_URL}/api/invoices`,
        tempInvoiceData,
        { withCredentials: true }
      );
      
      const response = await axios.post(
        `${API_URL}/api/invoices/${saveResponse.data.id}/pdf`,
        {},
        { responseType: 'blob', withCredentials: true }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${formData.invoice_number || 'draft'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
      localStorage.removeItem(INVOICE_STORAGE_KEY);
      fetchPlanInfo();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setPricingTrigger('no_credits');
        setShowPricing(true);
      } else {
        toast.error('Failed to download PDF');
      }
    }
    setLoading(false);
  };

  const getCurrencySymbol = () => {
    return CURRENCIES.find(c => c.value === formData.currency)?.symbol || '$';
  };

  const template = TEMPLATES.find(t => t.id === selectedTemplate);

  return (
    <div className="min-h-screen bg-[#050507] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Professional</span>
          <h1 className="font-clash text-4xl sm:text-5xl font-medium text-white mt-2 mb-4" data-testid="invoice-creator-title">
            Invoice Creator
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Create stunning, professional invoices with protective terms. Choose from 10 templates designed for creatives.
          </p>
          <div className="mt-8 max-w-2xl mx-auto overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/vjo2oo2e_pexels-kindelmedia-7688123.jpg" 
              alt="Person reviewing invoice" 
              className="w-full h-48 sm:h-64 object-cover opacity-60"
              data-testid="invoice-creator-hero-image"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Template Selector */}
          <div className="lg:col-span-1">
            <div className="bg-surface p-4 rounded-md border border-white/8 card-glow sticky top-24">
              <h3 className="font-medium text-white mb-4">Templates</h3>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-2">
                  {TEMPLATES.map((t) => {
                    const isLocked = !allowedTemplates.includes(t.id);
                    return (
                    <button
                      key={t.id}
                      onClick={() => {
                        if (isLocked) {
                          setPricingTrigger('template');
                          setShowPricing(true);
                        } else {
                          setSelectedTemplate(t.id);
                        }
                      }}
                      className={`w-full p-3 rounded-md border text-left transition-all relative ${
                        selectedTemplate === t.id
                          ? 'border-[#FF4500] bg-[#FF4500]/10'
                          : isLocked
                            ? 'border-white/5 opacity-60'
                            : 'border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      <div 
                        className="w-full h-12 rounded mb-2"
                        style={{ backgroundColor: t.color, border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white/70">{t.name}</span>
                        {isLocked && <Lock className="w-3.5 h-3.5 text-white/40" />}
                      </div>
                    </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow space-y-6">
              {/* Logo Upload */}
              <div>
                <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                  <Image className="w-4 h-4 text-[#FF4500]" />
                  Business Logo
                </h3>
                <div className="space-y-4">
                  {/* Logo Preview */}
                  {logoPreview ? (
                    <div className="relative">
                      <div 
                        className={`p-4 bg-black/30 rounded-md flex ${
                          formData.logo_position === 'center' ? 'justify-center' : 
                          formData.logo_position === 'right' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <img 
                          src={logoPreview} 
                          alt="Logo preview" 
                          className="max-h-20 max-w-[150px] object-contain"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 h-6 w-6 bg-black/50 hover:bg-red-500/50 text-white/70"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-white/10 rounded-md p-6 text-center cursor-pointer hover:border-[#FF4500]/50 transition-colors"
                      >
                        <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                        <p className="text-sm text-white/50">Click to upload logo</p>
                        <p className="text-xs text-white/30 mt-1">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      
                      {/* Logo Library Button */}
                      {savedLogos.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowLogoLibrary(true)}
                          className="w-full border-white/10 text-white hover:bg-white/5"
                          data-testid="open-logo-library"
                        >
                          <Library className="w-4 h-4 mr-2" />
                          Choose from Library ({savedLogos.length})
                        </Button>
                      )}
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                  
                  {uploadingLogo && (
                    <div className="flex items-center gap-2 text-sm text-white/50">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </div>
                  )}

                  {/* Logo Position */}
                  {logoPreview && (
                    <div className="space-y-2">
                      <Label className="text-white/70">Logo Position</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={formData.logo_position === 'left' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleInputChange('logo_position', 'left')}
                          className={formData.logo_position === 'left' 
                            ? 'bg-[#FF4500] hover:bg-[#CC3700]' 
                            : 'border-white/10 text-white hover:bg-white/5'}
                          data-testid="logo-position-left"
                        >
                          <AlignLeft className="w-4 h-4 mr-1" />
                          Left
                        </Button>
                        <Button
                          type="button"
                          variant={formData.logo_position === 'center' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleInputChange('logo_position', 'center')}
                          className={formData.logo_position === 'center' 
                            ? 'bg-[#FF4500] hover:bg-[#CC3700]' 
                            : 'border-white/10 text-white hover:bg-white/5'}
                          data-testid="logo-position-center"
                        >
                          <AlignCenter className="w-4 h-4 mr-1" />
                          Center
                        </Button>
                        <Button
                          type="button"
                          variant={formData.logo_position === 'right' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleInputChange('logo_position', 'right')}
                          className={formData.logo_position === 'right' 
                            ? 'bg-[#FF4500] hover:bg-[#CC3700]' 
                            : 'border-white/10 text-white hover:bg-white/5'}
                          data-testid="logo-position-right"
                        >
                          <AlignRight className="w-4 h-4 mr-1" />
                          Right
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Details */}
              <div>
                <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                  <Type className="w-4 h-4 text-[#FF4500]" />
                  Font & Layout
                </h3>
                {/* Font Selection */}
                <div className="space-y-3 mb-4">
                  <Label className="text-white/70">Font Style</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {FONTS.map((font) => (
                      <button
                        key={font.id}
                        onClick={() => handleInputChange('font_family', font.id)}
                        className={`w-full p-3 rounded-md border text-left transition-all ${
                          formData.font_family === font.id
                            ? 'border-[#FF4500] bg-[#FF4500]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`font-${font.id.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <span className="text-sm text-white" style={{ fontFamily: font.preview }}>{font.name}</span>
                        <span className="text-[10px] text-white/40 ml-2">{font.category}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {/* Header Style */}
                <div className="space-y-3">
                  <Label className="text-white/70">Header Style</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {HEADER_STYLES.map((hs) => (
                      <button
                        key={hs.id}
                        onClick={() => handleInputChange('header_style', hs.id)}
                        className={`p-3 rounded-md border text-left transition-all ${
                          formData.header_style === hs.id
                            ? 'border-[#FF4500] bg-[#FF4500]/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`header-style-${hs.id}`}
                      >
                        <span className="text-sm text-white block">{hs.name}</span>
                        <span className="text-[10px] text-white/40">{hs.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* My Templates */}
              {isAuthenticated && (
                <div>
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <LayoutTemplate className="w-4 h-4 text-[#FF4500]" />
                    My Templates
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSavePreset(true)}
                      className="flex-1 border-white/10 text-white/70 hover:bg-white/5"
                      data-testid="save-template-btn"
                    >
                      <BookmarkPlus className="w-4 h-4 mr-1.5" />
                      Save as Template
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLoadPreset(true)}
                      disabled={savedPresets.length === 0}
                      className="flex-1 border-white/10 text-white/70 hover:bg-white/5"
                      data-testid="load-template-btn"
                    >
                      <FolderOpen className="w-4 h-4 mr-1.5" />
                      Load ({savedPresets.length})
                    </Button>
                  </div>
                </div>
              )}

              {/* Original Business Details */}
              <div>
                <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#FF4500]" />
                  Your Business
                </h3>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/70">Business Name *</Label>
                    <Input
                      placeholder="Your Business Name"
                      value={formData.business_name}
                      onChange={(e) => handleInputChange('business_name', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="input-inv-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Address</Label>
                    <Input
                      placeholder="123 Music Street, City"
                      value={formData.business_address}
                      onChange={(e) => handleInputChange('business_address', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Email</Label>
                      <Input
                        type="email"
                        placeholder="you@email.com"
                        value={formData.business_email}
                        onChange={(e) => handleInputChange('business_email', e.target.value)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Phone</Label>
                      <Input
                        placeholder="+1 234 567 890"
                        value={formData.business_phone}
                        onChange={(e) => handleInputChange('business_phone', e.target.value)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Banking Details (Optional) */}
              <div>
                <button
                  type="button"
                  onClick={() => handleInputChange('include_banking', !formData.include_banking)}
                  className={`w-full flex items-center justify-between p-3 rounded-md border transition-all ${
                    formData.include_banking
                      ? 'border-[#FF4500] bg-[#FF4500]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid="toggle-banking-details"
                >
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-[#FF4500]" />
                    <span className="text-sm font-medium text-white">Banking Details</span>
                    <span className="text-[10px] text-white/40">(optional)</span>
                  </div>
                  <div className={`w-8 h-4 rounded-full transition-colors ${formData.include_banking ? 'bg-[#FF4500]' : 'bg-white/15'}`}>
                    <div className={`w-3.5 h-3.5 rounded-full bg-white transition-transform mt-[1px] ${formData.include_banking ? 'translate-x-[17px]' : 'translate-x-[1px]'}`} />
                  </div>
                </button>
                {formData.include_banking && (
                  <div className="grid gap-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Account Number</Label>
                      <Input
                        placeholder="12345678"
                        value={formData.bank_account_number}
                        onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                        data-testid="input-bank-account"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Sort Code / IBAN</Label>
                      <Input
                        placeholder="00-00-00 or GB00XXXX..."
                        value={formData.bank_sort_code}
                        onChange={(e) => handleInputChange('bank_sort_code', e.target.value)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                        data-testid="input-bank-sort-code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">SWIFT / BIC</Label>
                      <Input
                        placeholder="ABCDGB2L"
                        value={formData.bank_swift}
                        onChange={(e) => handleInputChange('bank_swift', e.target.value)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                        data-testid="input-bank-swift"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Client Details */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#FF4500]" />
                    Client Details
                  </h3>
                  {isAuthenticated && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowClientManager(true)}
                      className="text-white/40 hover:text-white text-xs gap-1"
                      data-testid="manage-clients-btn"
                    >
                      <Users className="w-3 h-3" />
                      Manage
                    </Button>
                  )}
                </div>

                {/* Saved Client Search */}
                {isAuthenticated && savedClients.length > 0 && (
                  <div className="relative mb-4" ref={clientSearchRef}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <Input
                        placeholder="Search saved clients..."
                        value={clientSearch}
                        onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 pl-9"
                        data-testid="client-search-input"
                      />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    </div>
                    {showClientDropdown && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#121216] border border-white/10 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {filteredClients.length === 0 ? (
                          <div className="px-3 py-2 text-white/40 text-sm">No matching clients</div>
                        ) : (
                          filteredClients.map(client => (
                            <button
                              key={client.id}
                              onClick={() => handleSelectClient(client)}
                              className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors"
                              data-testid={`select-client-${client.id}`}
                            >
                              <p className="text-sm text-white font-medium">{client.name}</p>
                              <p className="text-[10px] text-white/40">
                                {[client.email, client.address].filter(Boolean).join(' · ') || 'No details'}
                              </p>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/70">Client Name *</Label>
                    <Input
                      placeholder="Client / Company Name"
                      value={formData.client_name}
                      onChange={(e) => handleInputChange('client_name', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                      data-testid="input-inv-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Client Address</Label>
                    <Input
                      placeholder="Client Address"
                      value={formData.client_address}
                      onChange={(e) => handleInputChange('client_address', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Client Email</Label>
                    <Input
                      type="email"
                      placeholder="client@email.com"
                      value={formData.client_email}
                      onChange={(e) => handleInputChange('client_email', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                </div>
                {/* Save Client button */}
                {isAuthenticated && formData.client_name && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSaveClient}
                    className="mt-3 text-white/40 hover:text-white text-xs gap-1.5 w-full border border-dashed border-white/10 hover:border-white/20"
                    data-testid="save-client-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Save client for future invoices
                  </Button>
                )}
              </div>

              {/* Invoice Details */}
              <div>
                <h3 className="font-medium text-white mb-4">Invoice Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/70">Invoice Number</Label>
                    <Input
                      placeholder="Auto-generated"
                      value={formData.invoice_number}
                      onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => handleInputChange('currency', v)}>
                      <SelectTrigger className="bg-black/50 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121216] border-white/10 max-h-60 overflow-y-auto">
                        {CURRENCIES.map(c => (
                          <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/5">
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Invoice Date</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => handleInputChange('due_date', e.target.value)}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-white/70">Event Date (optional)</Label>
                    <Input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => handleInputChange('event_date', e.target.value)}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h3 className="font-medium text-white mb-4">Services / Items</h3>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5 space-y-1">
                        {index === 0 && <Label className="text-white/50 text-xs">Description</Label>}
                        <Input
                          placeholder="DJ Performance"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        {index === 0 && <Label className="text-white/50 text-xs">Qty</Label>}
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        {index === 0 && <Label className="text-white/50 text-xs">Rate</Label>}
                        <Input
                          type="number"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="bg-black/50 border-white/10 text-white"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        {index === 0 && <Label className="text-white/50 text-xs">Amount</Label>}
                        <Input
                          type="number"
                          value={item.amount}
                          readOnly
                          className="bg-black/30 border-white/10 text-white/70"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          className="text-white/40 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addItem}
                    className="w-full border-white/10 text-white/70 hover:bg-white/5"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </div>

              {/* Deposit */}
              <div className="space-y-2">
                <Label className="text-white/70">Deposit Paid</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0.00"
                  value={formData.deposit}
                  onChange={(e) => handleInputChange('deposit', parseFloat(e.target.value) || 0)}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                />
              </div>

              {/* Terms */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-white/70">Terms & Conditions</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleImproveTerms}
                    disabled={improvingTerms}
                    className="text-[#FF4500] hover:text-[#FF4500]/80 hover:bg-[#FF4500]/10"
                    data-testid="improve-terms-btn"
                  >
                    {improvingTerms ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Improve with AI
                  </Button>
                </div>
                <Textarea
                  value={formData.terms}
                  onChange={(e) => handleInputChange('terms', e.target.value)}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30 min-h-[150px]"
                  data-testid="input-terms"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-white/70">Additional Notes</Label>
                <Textarea
                  placeholder="Thank you for your business!"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveInvoice}
                  disabled={loading || !isAuthenticated}
                  className="flex-1 bg-[#FF4500] hover:bg-[#CC3700] text-white"
                  data-testid="save-invoice-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Invoice
                </Button>
                <Button
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                  data-testid="download-invoice-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
              {!isAuthenticated && (
                <p className="text-center text-white/40 text-sm">Login to save invoices</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-2">
            <div className="bg-surface p-4 rounded-md border border-white/8 card-glow sticky top-24">
              <h3 className="font-medium text-white mb-4">Live Preview</h3>
              <div 
                className="rounded-md overflow-hidden relative blur-[1.5px]"
                style={{ backgroundColor: template?.color || '#ffffff', fontFamily: FONTS.find(f => f.id === formData.font_family)?.preview || "'Inter', sans-serif" }}
              >
                {/* Watermark overlay for free users */}
                {showWatermark && (
                  <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden opacity-[0.07]" style={{ transform: 'rotate(-30deg)' }}>
                    <div className="flex flex-wrap gap-x-8 gap-y-6 items-center justify-center h-[150%] w-[150%] -translate-x-[15%] -translate-y-[15%]">
                      {Array.from({ length: 40 }).map((_, i) => (
                        <span key={i} className="text-sm font-bold whitespace-nowrap" style={{ color: template?.textColor || '#000' }}>
                          Generated by SettledInFull
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-6 min-h-[600px]" style={{ color: template?.textColor || '#000000' }}>
                  {/* Logo (only shown outside banner) */}
                  {logoPreview && formData.header_style !== 'banner' && (
                    <div 
                      className={`mb-4 ${
                        formData.logo_position === 'center' ? 'text-center' : 
                        formData.logo_position === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      <img 
                        src={logoPreview} 
                        alt="Business Logo" 
                        className="inline-block max-h-16 max-w-[120px] object-contain"
                      />
                    </div>
                  )}
                  
                  {/* Header - varies by style */}
                  {formData.header_style === 'banner' ? (
                    <div className="-m-6 mb-6 p-6 flex justify-between items-start" style={{ backgroundColor: template?.textColor, color: template?.color }}>
                      <div>
                        {logoPreview && (
                          <div className="mb-2">
                            <img src={logoPreview} alt="Logo" className="inline-block max-h-12 max-w-[100px] object-contain" />
                          </div>
                        )}
                        <h2 className="text-2xl font-bold mb-1">INVOICE</h2>
                        <p className="text-lg font-semibold">{formData.business_name || 'Your Business'}</p>
                        <p className="text-sm opacity-80">{formData.business_address}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">#{formData.invoice_number || 'DRAFT'}</p>
                        <p className="text-sm opacity-80">Date: {formData.invoice_date}</p>
                        <p className="text-sm opacity-80">Due: {formData.due_date || 'Not set'}</p>
                      </div>
                    </div>
                  ) : formData.header_style === 'boxed' ? (
                    <div className="flex justify-between items-start mb-8 p-4 rounded-lg" style={{ border: `2px solid ${template?.textColor}` }}>
                      <div>
                        <h2 className="text-2xl font-bold mb-1" style={{ color: template?.textColor }}>INVOICE</h2>
                        <p className="text-lg font-semibold">{formData.business_name || 'Your Business'}</p>
                        <p className="text-sm opacity-70">{formData.business_address}</p>
                        <p className="text-sm opacity-70">{formData.business_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold" style={{ color: template?.textColor }}>#{formData.invoice_number || 'DRAFT'}</p>
                        <p className="text-sm opacity-70">Date: {formData.invoice_date}</p>
                        <p className="text-sm opacity-70">Due: {formData.due_date || 'Not set'}</p>
                      </div>
                    </div>
                  ) : formData.header_style === 'minimal' ? (
                    <div className="flex justify-between items-start mb-8 pb-4" style={{ borderBottom: `1px solid ${template?.textColor}30` }}>
                      <div>
                        <h2 className="text-2xl font-light tracking-widest mb-2" style={{ color: template?.textColor }}>INVOICE</h2>
                        <p className="text-lg font-medium">{formData.business_name || 'Your Business'}</p>
                        <p className="text-sm opacity-70">{formData.business_address}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold" style={{ color: template?.textColor }}>#{formData.invoice_number || 'DRAFT'}</p>
                        <p className="text-sm opacity-70">Date: {formData.invoice_date}</p>
                        <p className="text-sm opacity-70">Due: {formData.due_date || 'Not set'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start mb-8 pb-4" style={{ borderBottom: `2px solid ${template?.textColor}20` }}>
                      <div>
                        <h2 className="text-2xl font-bold mb-1" style={{ color: template?.textColor }}>INVOICE</h2>
                        <p className="text-lg font-semibold">{formData.business_name || 'Your Business'}</p>
                        <p className="text-sm opacity-70">{formData.business_address}</p>
                        <p className="text-sm opacity-70">{formData.business_email}</p>
                        <p className="text-sm opacity-70">{formData.business_phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">#{formData.invoice_number || 'DRAFT'}</p>
                        <p className="text-sm opacity-70">Date: {formData.invoice_date}</p>
                        <p className="text-sm opacity-70">Due: {formData.due_date || 'Not set'}</p>
                        {formData.event_date && <p className="text-sm opacity-70">Event: {formData.event_date}</p>}
                      </div>
                    </div>
                  )}

                  {/* Client */}
                  <div className="mb-8">
                    <p className="text-xs uppercase tracking-wider opacity-50 mb-1">Bill To</p>
                    <p className="font-semibold">{formData.client_name || 'Client Name'}</p>
                    <p className="text-sm opacity-70">{formData.client_address}</p>
                    <p className="text-sm opacity-70">{formData.client_email}</p>
                  </div>

                  {/* Banking Details */}
                  {formData.include_banking && (formData.bank_account_number || formData.bank_sort_code || formData.bank_swift) && (
                    <div className="mb-8">
                      <p className="text-xs uppercase tracking-wider opacity-50 mb-1">Banking Details</p>
                      {formData.bank_account_number && <p className="text-sm">Account: {formData.bank_account_number}</p>}
                      {formData.bank_sort_code && <p className="text-sm">Sort Code / IBAN: {formData.bank_sort_code}</p>}
                      {formData.bank_swift && <p className="text-sm">SWIFT / BIC: {formData.bank_swift}</p>}
                    </div>
                  )}

                  {/* Items Table */}
                  <div className="mb-8">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: template?.textColor, color: template?.color }}>
                          <th className="text-left p-2">Description</th>
                          <th className="text-center p-2">Qty</th>
                          <th className="text-right p-2">Rate</th>
                          <th className="text-right p-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index} style={{ borderBottom: `1px solid ${template?.textColor}20` }}>
                            <td className="p-2">{item.description || '-'}</td>
                            <td className="text-center p-2">{item.quantity}</td>
                            <td className="text-right p-2">{getCurrencySymbol()}{item.rate.toFixed(2)}</td>
                            <td className="text-right p-2">{getCurrencySymbol()}{item.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="flex justify-end mb-8">
                    <div className="w-48">
                      <div className="flex justify-between py-1">
                        <span className="opacity-70">Subtotal</span>
                        <span>{getCurrencySymbol()}{formData.subtotal.toFixed(2)}</span>
                      </div>
                      {formData.deposit > 0 && (
                        <div className="flex justify-between py-1">
                          <span className="opacity-70">Deposit</span>
                          <span>-{getCurrencySymbol()}{formData.deposit.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 font-bold text-lg" style={{ borderTop: `2px solid ${template?.textColor}` }}>
                        <span>Balance Due</span>
                        <span>{getCurrencySymbol()}{formData.balance_due.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  {formData.terms && (
                    <div className="mb-4">
                      <p className="font-semibold text-sm mb-1">Terms & Conditions</p>
                      <p className="text-xs opacity-70 whitespace-pre-line">{formData.terms}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {formData.notes && (
                    <div>
                      <p className="font-semibold text-sm mb-1">Notes</p>
                      <p className="text-xs opacity-70">{formData.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl flex items-center gap-2">
              <Crop className="w-5 h-5 text-[#FF4500]" />
              Crop & Resize Logo
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-white/60 mb-4">
              Drag to select the area you want to use for your logo
            </p>
            <div className="flex justify-center bg-black/30 rounded-md p-4 max-h-[400px] overflow-auto">
              {cropImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                >
                  <img
                    src={cropImage}
                    onLoad={onImageLoad}
                    alt="Crop preview"
                    style={{ maxHeight: '350px', maxWidth: '100%' }}
                  />
                </ReactCrop>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCropDialog(false);
                setCropImage(null);
              }}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCropComplete}
              className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
              data-testid="apply-crop-btn"
            >
              <Crop className="w-4 h-4 mr-2" />
              Apply Crop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logo Library Dialog */}
      <Dialog open={showLogoLibrary} onOpenChange={setShowLogoLibrary}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl flex items-center gap-2">
              <Library className="w-5 h-5 text-[#FF4500]" />
              Logo Library
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-white/60 mb-4">
              Select a previously uploaded logo to use
            </p>
            
            {savedLogos.length === 0 ? (
              <div className="text-center py-8">
                <Image className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No logos saved yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                {savedLogos.map((logo) => (
                  <div
                    key={logo.id}
                    className="relative p-3 bg-black/30 rounded-md border border-white/10 hover:border-[#FF4500] transition-colors group"
                  >
                    <button
                      onClick={() => handleSelectFromLibrary(logo)}
                      className="w-full"
                    >
                      <img
                        src={`${API_URL}/api/files/${logo.storage_path}`}
                        alt={logo.original_filename}
                        className="w-full h-16 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <p className="text-xs text-white/50 mt-2 truncate group-hover:text-white/70">
                        {logo.original_filename}
                      </p>
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm('Delete this logo?')) return;
                        try {
                          await axios.delete(`${API_URL}/api/files/${logo.id}`, { withCredentials: true });
                          setSavedLogos(prev => prev.filter(l => l.id !== logo.id));
                          toast.success('Logo deleted');
                        } catch { toast.error('Failed to delete'); }
                      }}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white/50 hover:text-red-400"
                      title="Delete logo"
                      data-testid={`delete-logo-${logo.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowLogoLibrary(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowLogoLibrary(false);
                fileInputRef.current?.click();
              }}
              className="bg-[#FF4500] hover:bg-[#CC3700] text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload New
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        trigger={pricingTrigger}
      />

      {/* Save Template Dialog */}
      <Dialog open={showSavePreset} onOpenChange={setShowSavePreset}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-[#FF4500]" />
              Save as Template
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-white/60">
              Save your current font, header style, and business details as a reusable template.
            </p>
            <div className="space-y-2">
              <Label className="text-white/70">Template Name</Label>
              <Input
                placeholder="e.g. My DJ Invoice"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                data-testid="preset-name-input"
              />
            </div>
            <div className="text-xs text-white/40 space-y-1">
              <p>Will save: Template #{selectedTemplate}, {formData.font_family} font, {formData.header_style} header</p>
              {formData.business_name && <p>Business: {formData.business_name}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSavePreset(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleSavePreset} className="bg-[#FF4500] hover:bg-[#CC3700] text-white" data-testid="confirm-save-template">
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={showLoadPreset} onOpenChange={setShowLoadPreset}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#FF4500]" />
              My Templates
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {savedPresets.length === 0 ? (
              <div className="text-center py-8">
                <LayoutTemplate className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No saved templates</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {savedPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between p-3 rounded-md border border-white/10 hover:border-[#FF4500]/50 transition-colors group"
                  >
                    <button
                      onClick={() => handleLoadPreset(preset)}
                      className="flex-1 text-left"
                      data-testid={`load-preset-${preset.id}`}
                    >
                      <p className="text-sm font-medium text-white">{preset.name}</p>
                      <p className="text-[10px] text-white/40 mt-0.5">
                        Template #{preset.template_id} &middot; {preset.font_family} &middot; {preset.header_style}
                        {preset.business_name ? ` &middot; ${preset.business_name}` : ''}
                      </p>
                    </button>
                    <button
                      onClick={() => handleDeletePreset(preset.id)}
                      className="ml-2 p-1.5 rounded text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-preset-${preset.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadPreset(false)} className="border-white/10 text-white hover:bg-white/5">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Manager Dialog */}
      <Dialog open={showClientManager} onOpenChange={setShowClientManager}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-[#FF4500]" />
              Saved Clients
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {/* Add / Edit form */}
            <div className="p-3 rounded-md border border-white/10 space-y-3">
              <p className="text-xs text-white/50 font-medium uppercase tracking-wider">{editingClient ? 'Edit Client' : 'Add New Client'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-white/60 text-xs">Name *</Label>
                  <Input placeholder="Contact name" value={clientForm.name} onChange={e => setClientForm(p => ({ ...p, name: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white/60 text-xs">Company</Label>
                  <Input placeholder="Company name" value={clientForm.company} onChange={e => setClientForm(p => ({ ...p, company: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-company" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white/60 text-xs">Email</Label>
                  <Input placeholder="email@example.com" value={clientForm.email} onChange={e => setClientForm(p => ({ ...p, email: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white/60 text-xs">Phone</Label>
                  <Input placeholder="+44 7..." value={clientForm.phone} onChange={e => setClientForm(p => ({ ...p, phone: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-white/60 text-xs">VAT / Tax No.</Label>
                  <Input placeholder="GB123456789" value={clientForm.vat_number} onChange={e => setClientForm(p => ({ ...p, vat_number: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-vat" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-white/60 text-xs">Address</Label>
                  <Input placeholder="Billing address" value={clientForm.address} onChange={e => setClientForm(p => ({ ...p, address: e.target.value }))} className="bg-black/50 border-white/10 text-white placeholder:text-white/30 h-9 text-sm" data-testid="client-form-address" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveClientFromManager} className="bg-[#FF4500] hover:bg-[#CC3700] text-white text-xs" data-testid="client-form-save">
                  <Save className="w-3 h-3 mr-1" />
                  {editingClient ? 'Update' : 'Add Client'}
                </Button>
                {editingClient && (
                  <Button size="sm" variant="ghost" onClick={() => { setEditingClient(null); setClientForm({ name: '', company: '', email: '', phone: '', address: '', vat_number: '' }); }} className="text-white/40 text-xs">Cancel</Button>
                )}
              </div>
            </div>

            {/* Client List */}
            {savedClients.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-10 h-10 text-white/15 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No saved clients yet</p>
              </div>
            ) : (
              <div className="space-y-1 max-h-[250px] overflow-y-auto">
                {savedClients.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-2.5 rounded-md border border-white/6 hover:border-white/15 transition-colors group">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{client.name}</p>
                      <p className="text-[10px] text-white/40 truncate">
                        {[client.company, client.email, client.phone, client.vat_number].filter(Boolean).join(' · ') || 'No details'}
                      </p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                      <button
                        onClick={() => { setEditingClient(client); setClientForm({ name: client.name || '', company: client.company || '', email: client.email || '', phone: client.phone || '', address: client.address || '', vat_number: client.vat_number || '' }); }}
                        className="p-1.5 rounded text-white/30 hover:text-blue-400"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-1.5 rounded text-white/30 hover:text-red-400"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowClientManager(false); setEditingClient(null); setClientForm({ name: '', company: '', email: '', phone: '', address: '', vat_number: '' }); }} className="border-white/10 text-white hover:bg-white/5">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
